import { AttachFile, Check, Close, InsertDriveFileOutlined, Send } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputBase,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { UserAvatar } from '@/components/common/UserAvatar';
import ChatAttachmentDisplay from '@/components/dashboard/chat/ChatAttachmentDisplay';
import { useAuthContext } from '@/context/AuthProvider';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useChatFileUpload } from '@/hooks/useChatFileUpload';
import { useClientGroupChat } from '@/hooks/useClientGroupChat';
import { useClientGroups } from '@/hooks/useClientGroups';
import { useUserRole } from '@/hooks/useUserRole';
import { ChatMessageStatus } from '@/models/ChatMessage';
import { ChatAttachment, GroupChatMessage } from '@/models/GroupChatMessage';
import { getPublicUserProfile } from '@/services/api/users/api';
import { useTranslationPrefix } from '@/translations/i18n';
import { fetchAddressSuggestions } from '@/types/maps';

interface AdvisorChatTabProps {
  onClose: () => void;
  selectedGroupId?: string | null;
  propertyPickerOpen?: boolean;
  onClosePropertyPicker?: () => void;
}

/* ─── Format time as absolute (e.g. "9:14 AM") ─────────── */

const midnightOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const yesterdayMidnight = (now: Date) =>
  new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).getTime();

const formatAbsoluteTime = (timestamp: number): string =>
  new Date(timestamp).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

/* ─── Format day label (e.g. "Today", "Yesterday", "Tue, April 14") ── */

const formatDayLabel = (timestamp: number): string => {
  const date = new Date(timestamp);
  const now = new Date();
  if (midnightOf(date) === midnightOf(now)) return 'Today';
  if (midnightOf(date) === yesterdayMidnight(now)) return 'Yesterday';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric' });
};

/* ─── Day separator component ────────────────────────────── */

const DaySeparator = ({ label }: { label: string }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      py: 1.5,
    }}>
    <Typography
      sx={{
        fontSize: 11,
        color: 'var(--tx-faint)',
        fontWeight: 500,
        letterSpacing: '0.3px',
      }}>
      {label}
    </Typography>
  </Box>
);

/* ─── Message bubble with avatar ─────────────────────────── */

const MessageBubble = ({
  message,
  isOwn,
  advisorName,
  advisorPhotoUrl,
  showAvatar,
  showName,
  currentUserId,
  onToggleReaction,
  showNewMessageCue,
}: {
  message: GroupChatMessage;
  isOwn: boolean;
  advisorName: string;
  advisorPhotoUrl?: string;
  showAvatar: boolean;
  showName: boolean;
  currentUserId?: string;
  onToggleReaction?: (createdAt: number, emoji: string, action: 'add' | 'remove') => void;
  showNewMessageCue?: boolean;
}) => {
  const isDeleted = message.status === ChatMessageStatus.Deleted;
  const reactionMap =
    message.reactions?.reduce((acc, r) => {
      acc[r.emoji] = (acc[r.emoji] || 0) + 1;
      return acc;
    }, {} as Record<string, number>) ?? {};
  const reactionEntries = Object.entries(reactionMap);

  return (
    <Box sx={{ mb: 0.5 }}>
      {/* Sender name for other's messages — shown above first bubble in cluster */}
      {!isOwn && showName && (
        <Typography
          sx={{
            fontSize: 11,
            color: 'var(--tx-faint)',
            mb: '4px',
            ml: '44px',
          }}>
          {message.senderName}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: isOwn ? 'flex-end' : 'flex-start',
          alignItems: 'flex-end',
          gap: '8px',
        }}>
        {/* Avatar for other's messages */}
        {!isOwn && (
          <Box sx={{ width: 32, flexShrink: 0 }}>
            {showAvatar && (
              <Box sx={{ position: 'relative', width: 32, height: 32 }}>
                {showNewMessageCue && (
                  <Box
                    data-testid="advisor-chat-new-message-cue"
                    sx={{
                      position: 'absolute',
                      left: -5,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      backgroundColor: 'var(--tx-lime)',
                      border: '2px solid var(--tx-line)',
                      zIndex: 2,
                    }}
                  />
                )}
                <UserAvatar
                  name={advisorName}
                  photoUrl={advisorPhotoUrl}
                  size={32}
                  sx={{ backgroundColor: '#555' }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Bubble + reactions wrapper */}
        <Box
          sx={{
            position: 'relative',
            maxWidth: 'calc(100% - 40px)',
            paddingTop: reactionEntries.length > 0 ? '14px' : 0,
          }}>
          {/* Reaction badges — iMessage style, overlapping top corner */}
          {reactionEntries.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: '2px',
                ...(isOwn ? { left: '-18px' } : { right: '-18px' }),
                display: 'flex',
                gap: '1px',
                background: '#3c3c3e',
                borderRadius: '12px',
                px: '5px',
                py: '2px',
                border: '1.5px solid var(--tx-line)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.45)',
                zIndex: 2,
              }}>
              {reactionEntries.map(([emoji, count]) => {
                const isMine = message.reactions?.some(
                  (r) => r.userId === currentUserId && r.emoji === emoji,
                );
                return (
                  <Box
                    key={emoji}
                    component="button"
                    onClick={() =>
                      onToggleReaction?.(message.createdAt, emoji, isMine ? 'remove' : 'add')
                    }
                    sx={{
                      background: 'transparent',
                      border: 'none',
                      padding: '0 2px',
                      cursor: 'pointer',
                      lineHeight: 1.4,
                      color: isMine ? 'var(--tx-lime-ink)' : 'var(--tx-text)',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                    }}>
                    <span>{emoji}</span>
                    {count > 1 && (
                      <Typography component="span" sx={{ fontSize: 11, color: 'inherit' }}>
                        {count}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
          <Box
            sx={{
              backgroundColor: isOwn ? 'var(--tx-fill-2)' : 'var(--tx-glass-panel)',
              color: isOwn ? 'var(--tx-text)' : 'var(--tx-text)',
              borderRadius: isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              px: '14px',
              py: '10px',
              fontSize: 13,
              lineHeight: 1.5,
              fontWeight: 400,
              fontStyle: isDeleted ? 'italic' : 'normal',
              opacity: isDeleted ? 0.5 : 1,
              whiteSpace: 'pre-wrap',
            }}>
            {isDeleted ? 'This message was deleted' : message.content}
            {!isDeleted &&
              message.attachments?.map((att) => (
                <ChatAttachmentDisplay
                  key={att.s3Key}
                  attachment={att}
                  groupId={message.groupId}
                  isOwn={isOwn}
                />
              ))}
          </Box>
        </Box>
      </Box>

      {/* Timestamp */}
      <Typography
        sx={{
          fontSize: 10,
          color: 'var(--tx-faint)',
          mt: '3px',
          textAlign: isOwn ? 'right' : 'left',
          ml: isOwn ? 0 : '44px',
          mr: isOwn ? '4px' : 0,
        }}>
        {formatAbsoluteTime(message.createdAt)}
        {message.edited && ' (edited)'}
      </Typography>
    </Box>
  );
};

/* ─── Main component ─────────────────────────────────────── */

const AdvisorChatTab = ({
  onClose: _onClose,
  selectedGroupId,
  propertyPickerOpen,
  onClosePropertyPicker,
}: AdvisorChatTabProps) => {
  const [inputValue, setInputValue] = useState('');
  const [actionBarMsgId, setActionBarMsgId] = useState<string | null>(null);
  const [editingCreatedAt, setEditingCreatedAt] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const placesLib = useMapsLibrary('places');

  const [addressQuery, setAddressQuery] = useState('');
  const [addressFeatures, setAddressFeatures] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [showAddressDropdown, setShowAddressDropdown] = useState(false);
  const [stagedAttachments, setStagedAttachments] = useState<ChatAttachment[]>([]);
  const [stagedPreviews, setStagedPreviews] = useState<{ file: File; objectUrl: string }[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [newIncomingCueMessageId, setNewIncomingCueMessageId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const actionBarRef = useRef<HTMLDivElement | null>(null);
  const touchHoldTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastObservedMessageIdRef = useRef<string | undefined>(undefined);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { user } = useAuthContext();
  const { isAdvisor } = useUserRole();
  const { groups, isLoading: isLoadingGroups } = useClientGroups();
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');

  // Use explicit selection, then first available
  const group = groups.find((g) => g.id === selectedGroupId) ?? groups[0];

  const {
    startUpload,
    validateFiles,
    uploadProgress,
    inProgress: isUploading,
    clearUploadProgress,
  } = useChatFileUpload(group?.id ?? '');

  const {
    messages: realMessages,
    sendMessage,
    sendDeleteMessage,
    sendEditMessage,
    sendReaction,
    markAsRead,
    isLoadingMessages,
    hasMoreMessages,
    loadMoreMessages,
    isFetchingMore,
  } = useClientGroupChat(group?.id);

  // Fetch public profiles for all advisors (primary + co-advisors) for both views:
  // - Client view: per-message lookup so co-advisor messages show the right avatar
  // - Advisor view: needed to distinguish co-advisor messages from member messages
  const allAdvisorIds = useMemo(() => {
    if (!group) return [] as string[];
    const ids = new Set<string>([group.advisorId, ...(group.coAdvisorIds ?? [])]);
    return [...ids];
  }, [group]);

  const advisorProfileResults = useQueries({
    queries: allAdvisorIds.map((id) => ({
      queryKey: ['public-profile', id],
      queryFn: () => getPublicUserProfile(id),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const advisorProfileMap = useMemo(() => {
    const map: Record<string, { name: string; photoUrl?: string }> = {};
    allAdvisorIds.forEach((id, i) => {
      const data = advisorProfileResults[i]?.data;
      if (data) map[id] = { name: data.name || 'Advisor', photoUrl: data.photoUrl };
    });
    return map;
  }, [allAdvisorIds, advisorProfileResults]);

  // Show empty state when no group exists
  const noGroup = !group && !isLoadingGroups;
  const messages = realMessages;

  // Group messages by day for separators
  const messagesWithSeparators = useMemo(() => {
    const result: {
      type: 'separator' | 'message';
      label?: string;
      message?: GroupChatMessage;
    }[] = [];
    let lastDay = '';

    for (const msg of messages) {
      const dayLabel = formatDayLabel(msg.createdAt);
      if (dayLabel !== lastDay) {
        result.push({ type: 'separator', label: dayLabel });
        lastDay = dayLabel;
      }
      result.push({ type: 'message', message: msg });
    }
    return result;
  }, [messages]);

  // Mark as read when the thread opens/changes and when new messages arrive
  // while this thread is visible. The sidebar unread dot/counter should only
  // represent messages waiting outside the open chat drawer; the in-drawer
  // local cue below handles "new while I'm already here."
  const lastMessageId = messages[messages.length - 1]?.messageId;
  useEffect(() => {
    if (group?.id) {
      markAsRead();
    }
  }, [group?.id, lastMessageId, markAsRead]);

  useEffect(() => {
    lastObservedMessageIdRef.current = undefined;
    setNewIncomingCueMessageId(null);
  }, [group?.id]);

  useEffect(() => {
    const latestMessage = messages[messages.length - 1];
    if (!latestMessage?.messageId) return;

    const previousMessageId = lastObservedMessageIdRef.current;
    lastObservedMessageIdRef.current = latestMessage.messageId;

    // Initial load shouldn't look like a brand-new incoming message.
    if (!previousMessageId || previousMessageId === latestMessage.messageId) return;

    const isIncoming = latestMessage.userId !== user?.id;
    if (!isIncoming) return;

    setNewIncomingCueMessageId(latestMessage.messageId);
  }, [messages, user?.id]);

  useEffect(
    () => () => {
      setNewIncomingCueMessageId(null);
    },
    [],
  );

  // Auto-scroll to bottom on new messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus input on mount (skip on mobile to avoid keyboard pushing UI off screen)
  useEffect(() => {
    if (!isMobile) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isMobile]);

  // Clean up object URLs on unmount to prevent memory leaks
  const stagedPreviewsRef = useRef(stagedPreviews);
  stagedPreviewsRef.current = stagedPreviews;
  useEffect(
    () => () => {
      stagedPreviewsRef.current.forEach((p) => {
        if (p.objectUrl) URL.revokeObjectURL(p.objectUrl);
      });
    },
    [],
  );

  const handleSend = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (isUploading) return;
      if (!trimmed && stagedAttachments.length === 0) return;
      setInputValue('');
      setNewIncomingCueMessageId(null);
      sendMessage(trimmed, undefined, stagedAttachments.length > 0 ? stagedAttachments : undefined);
      // Clean up staged files
      stagedPreviews.forEach((p) => {
        if (p.objectUrl) URL.revokeObjectURL(p.objectUrl);
      });
      setStagedAttachments([]);
      setStagedPreviews([]);
      clearUploadProgress();
    },
    [
      sendMessage,
      stagedAttachments,
      stagedPreviews,
      clearUploadProgress,
      isUploading,
    ],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(inputValue);
    }
  };

  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      if (isUploading || !group?.id) return;
      const error = validateFiles(files);
      if (error) {
        console.error('File validation failed', error);
        showAlert(t(error.messageKey, error.messageParams), 'error');
        return;
      }
      // Create previews, revoking any existing object URLs first
      const previews = files.map((file) => ({
        file,
        objectUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
      }));
      setStagedPreviews((prev) => {
        prev.forEach((p) => {
          if (p.objectUrl) URL.revokeObjectURL(p.objectUrl);
        });
        return previews;
      });

      const attachments = await startUpload(files);
      if (attachments) {
        setStagedAttachments(attachments);
      } else {
        // Upload failed — clean up all staged state
        previews.forEach((p) => {
          if (p.objectUrl) URL.revokeObjectURL(p.objectUrl);
        });
        setStagedPreviews([]);
        setStagedAttachments([]);
        clearUploadProgress();
        showAlert(t('file.upload_failed'), 'error');
      }
    },
    [validateFiles, startUpload, showAlert, isUploading, group?.id],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) handleFilesSelected(files);
      // Reset the input so re-selecting the same file triggers onChange
      e.target.value = '';
    },
    [handleFilesSelected],
  );

  const removeStagedFile = useCallback((index: number) => {
    setStagedPreviews((prev) => {
      const removed = prev[index];
      if (removed?.objectUrl) URL.revokeObjectURL(removed.objectUrl);
      return prev.filter((_, i) => i !== index);
    });
    setStagedAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounterRef.current = 0;
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) handleFilesSelected(files);
    },
    [handleFilesSelected],
  );

  // Reset address picker state when closed
  useEffect(() => {
    if (!propertyPickerOpen) {
      setAddressQuery('');
      setAddressFeatures([]);
      setShowAddressDropdown(false);
    }
  }, [propertyPickerOpen]);

  // Google Maps address autocomplete
  useEffect(() => {
    if (!propertyPickerOpen || !addressQuery || addressQuery.length < 3) {
      setAddressFeatures([]);
      setShowAddressDropdown(false);
      return;
    }
    if (!placesLib) return;

    const id = setTimeout(async () => {
      try {
        const suggestions = await fetchAddressSuggestions(addressQuery, placesLib);
        setAddressFeatures(suggestions);
        setShowAddressDropdown(suggestions.length > 0);
      } catch {
        setAddressFeatures([]);
        setShowAddressDropdown(false);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [addressQuery, propertyPickerOpen, placesLib]);

  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const isAwayFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight > 120;
    setShowScrollDown(isAwayFromBottom);
  }, []);

  const scrollToLatestMessage = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Dismiss action bar when clicking outside
  useEffect(() => {
    if (!actionBarMsgId) return;
    const handler = (e: MouseEvent) => {
      if (actionBarRef.current && !actionBarRef.current.contains(e.target as Node)) {
        setActionBarMsgId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [actionBarMsgId]);

  const handleAddressSelect = (prediction: google.maps.places.AutocompletePrediction) => {
    sendMessage(`I'd like to submit a property for review: ${prediction.description}`);
    onClosePropertyPicker?.();
  };

  // Loading state
  if (isLoadingGroups) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <CircularProgress size={28} sx={{ color: 'rgba(243,243,243,0.4)' }} />
      </Box>
    );
  }

  // No group
  if (noGroup) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 4,
          gap: 1.5,
        }}>
        <Typography
          sx={{
            color: 'var(--tx-muted)',
            fontSize: 15,
            fontWeight: 500,
            textAlign: 'center',
          }}>
          No advisor connected
        </Typography>
        <Typography
          sx={{
            color: 'var(--tx-faint)',
            fontSize: 13,
            textAlign: 'center',
            lineHeight: 1.5,
          }}>
          When an advisor is assigned to this property, you&apos;ll be able to message them here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, position: 'relative' }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}>
      {/* Drag overlay */}
      {isDragging && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 20,
            backgroundColor: 'var(--tx-glass)',
            boxShadow: 'var(--tx-shadow-sm)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px dashed rgba(192,255,2,0.5)',
            borderRadius: '12px',
            m: 1,
          }}>
          <Typography sx={{ fontSize: 14, color: '#C0FF02', fontWeight: 500 }}>
            Drop files here
          </Typography>
        </Box>
      )}

      {/* Property address picker */}
      {propertyPickerOpen && (
        <Box sx={{ mx: '16px', mb: '8px', position: 'relative', flexShrink: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              px: '12px',
              height: 40,
              borderRadius: '10px',
              backgroundColor: showAddressDropdown ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
              border: 0,
              transition: 'background-color 0.15s',
            }}>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#bbb7af"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0 }}>
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <InputBase
              autoFocus
              value={addressQuery}
              onChange={(e) => setAddressQuery(e.target.value)}
              onBlur={() => setTimeout(() => setShowAddressDropdown(false), 150)}
              onFocus={() => {
                if (addressFeatures.length > 0) setShowAddressDropdown(true);
              }}
              placeholder="Enter property address…"
              sx={{
                flex: 1,
                '& .MuiInputBase-input': {
                  fontSize: '13px',
                  fontFamily: 'Manrope, sans-serif',
                  color: 'var(--tx-text)',
                  '&::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
                },
              }}
            />
            <IconButton
              size="small"
              onClick={onClosePropertyPicker}
              sx={{ color: '#6b6766', p: '2px', '&:hover': { color: 'var(--tx-muted)' } }}>
              <Close sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
          {showAddressDropdown && addressFeatures.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 44,
                left: 0,
                right: 0,
                zIndex: 10,
                backgroundColor: 'var(--tx-glass)',
                backdropFilter: 'var(--tx-blur)',
                borderRadius: '12px',
                boxShadow: 'var(--tx-shadow-sm)',
                overflow: 'hidden',
              }}>
              {addressFeatures.map((prediction) => (
                <Box
                  key={prediction.place_id}
                  onMouseDown={() => handleAddressSelect(prediction)}
                  sx={{
                    px: '12px',
                    py: '10px',
                    fontSize: 13,
                    color: 'var(--tx-text)',
                    fontFamily: 'Manrope, sans-serif',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                    borderBottom: '1px solid var(--tx-line)',
                    '&:last-child': { borderBottom: 'none' },
                  }}>
                  {prediction.description}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Scroll-to-bottom button */}
      {showScrollDown && (
        <Box
          component="button"
          onClick={scrollToLatestMessage}
          sx={{
            position: 'absolute',
            bottom: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'var(--tx-glass-panel)',
            backgroundImage: 'var(--tx-surface-light)',
            backdropFilter: 'blur(8px)',
            boxShadow: 'var(--tx-shadow)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': { background: 'rgba(60,60,60,0.95)' },
          }}>
          <svg
            width={14}
            height={14}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#f3f3f3"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </Box>
      )}

      {/* Messages area */}
      <Box
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: '20px',
          pt: '4px',
          pb: '8px',
          display: 'flex',
          flexDirection: 'column',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}>
        {/* Load more button */}
        {hasMoreMessages && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
            <Button
              data-testid="advisor-chat-load-more-btn"
              onClick={() => loadMoreMessages()}
              disabled={isFetchingMore}
              size="small"
              sx={{
                color: 'var(--tx-faint)',
                fontSize: 12,
                textTransform: 'none',
                '&:hover': { color: 'var(--tx-text)' },
              }}>
              {isFetchingMore ? (
                <CircularProgress size={14} sx={{ color: 'rgba(243,243,243,0.4)', mr: 1 }} />
              ) : null}
              Load earlier messages
            </Button>
          </Box>
        )}

        {/* Loading messages spinner */}
        {isLoadingMessages && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <CircularProgress size={24} sx={{ color: 'rgba(243,243,243,0.4)' }} />
          </Box>
        )}

        {/* Messages with day separators */}
        {messagesWithSeparators.map((item, idx) => {
          if (item.type === 'separator') {
            return <DaySeparator key={`sep-${idx}`} label={item.label ?? ''} />;
          }

          const msg = item.message;
          if (!msg) return null;
          const currentUserId = user?.id;
          const isOwn = msg.userId === currentUserId;
          const isEditing = editingCreatedAt === msg.createdAt;

          // iOS-style: name above FIRST bubble in cluster; avatar next to LAST bubble in cluster
          const prevItem = messagesWithSeparators[idx - 1];
          const nextItem = messagesWithSeparators[idx + 1];
          const showName =
            prevItem?.type === 'separator' ||
            (prevItem?.type === 'message' && prevItem.message?.userId !== msg.userId);
          const showAvatar =
            nextItem === undefined ||
            nextItem.type === 'separator' ||
            (nextItem.type === 'message' && nextItem.message?.userId !== msg.userId);

          // Resolve avatar per sender:
          // - Advisor view: co-advisor messages use the co-advisor's profile;
          //   member (client) messages use the member's group record.
          // - Client view: all non-own messages are from an advisor — look up by userId.
          const isSenderAdvisor = allAdvisorIds.includes(msg.userId);
          const msgAdvisorName = isAdvisor
            ? isSenderAdvisor
              ? advisorProfileMap[msg.userId]?.name ?? msg.senderName ?? 'Advisor'
              : group?.members[0]?.name ?? 'Client'
            : advisorProfileMap[msg.userId]?.name ?? msg.senderName ?? 'Advisor';
          const msgAdvisorPhotoUrl = isAdvisor
            ? isSenderAdvisor
              ? advisorProfileMap[msg.userId]?.photoUrl
              : group?.members[0]?.photoUrl
            : advisorProfileMap[msg.userId]?.photoUrl;

          return (
            <Box
              key={msg.messageId}
              data-testid={`advisor-chat-message-${msg.messageId}`}
              tabIndex={0}
              onContextMenu={(e) => {
                e.preventDefault();
                setActionBarMsgId((prev) => (prev === msg.messageId ? null : msg.messageId));
              }}
              onTouchStart={() => {
                touchHoldTimerRef.current = setTimeout(() => {
                  setActionBarMsgId(msg.messageId);
                }, 500);
              }}
              onTouchEnd={() => {
                if (touchHoldTimerRef.current) {
                  clearTimeout(touchHoldTimerRef.current);
                  touchHoldTimerRef.current = null;
                }
              }}
              onTouchMove={() => {
                if (touchHoldTimerRef.current) {
                  clearTimeout(touchHoldTimerRef.current);
                  touchHoldTimerRef.current = null;
                }
              }}>
              {isEditing ? (
                <Box sx={{ ml: isOwn ? 'auto' : '44px', maxWidth: '75%' }}>
                  <InputBase
                    data-testid="advisor-chat-edit-input"
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (editDraft.trim()) sendEditMessage(msg.createdAt, editDraft.trim());
                        setEditingCreatedAt(null);
                      }
                      if (e.key === 'Escape') setEditingCreatedAt(null);
                    }}
                    autoFocus
                    multiline
                    minRows={1}
                    fullWidth
                    sx={{
                      backgroundColor: 'var(--tx-fill-2)',
                      color: 'var(--tx-text)',
                      borderRadius: '12px',
                      px: '14px',
                      py: '10px',
                      fontSize: 13,
                      lineHeight: 1.5,
                      '& textarea': { fontSize: 13, lineHeight: 1.5 },
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: '6px', mt: '6px', justifyContent: 'flex-end' }}>
                    <IconButton
                      data-testid="advisor-chat-edit-cancel-btn"
                      size="small"
                      onClick={() => setEditingCreatedAt(null)}
                      sx={{
                        width: 28,
                        height: 28,
                        backgroundColor: 'var(--tx-fill-3)',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.28)' },
                      }}>
                      <Close sx={{ fontSize: 14, color: 'var(--tx-text)' }} />
                    </IconButton>
                    <IconButton
                      data-testid="advisor-chat-edit-save-btn"
                      size="small"
                      onClick={() => {
                        if (editDraft.trim()) sendEditMessage(msg.createdAt, editDraft.trim());
                        setEditingCreatedAt(null);
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        backgroundColor: 'var(--tx-lime)',
                        '&:hover': { backgroundColor: '#D4FF4D' },
                      }}>
                      <Check sx={{ fontSize: 14, color: '#262626' }} />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                  {/* Glassmorphic action bar — right-click / long-press */}
                  {actionBarMsgId === msg.messageId && (
                    <Box
                      ref={actionBarRef}
                      sx={{
                        position: 'absolute',
                        bottom: 'calc(100% + 6px)',
                        ...(isOwn ? { right: 0 } : { left: '44px' }),
                        display: 'flex',
                        gap: '4px',
                        alignItems: 'center',
                        background: 'var(--tx-fill-3)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        border: 0,
                        borderRadius: '10px',
                        padding: '4px 6px',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                        zIndex: 10,
                        whiteSpace: 'nowrap',
                      }}>
                      {['👍', '❤️', '😂', '😢', '👏'].map((emoji) => (
                        <Box
                          key={emoji}
                          component="button"
                          onClick={() => {
                            sendReaction(msg.createdAt, emoji, 'add');
                            setActionBarMsgId(null);
                          }}
                          sx={{
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '2px 4px',
                            fontSize: '16px',
                            cursor: 'pointer',
                            lineHeight: 1,
                            transition: 'transform 0.1s',
                            '&:hover': { transform: 'scale(1.25)' },
                          }}>
                          {emoji}
                        </Box>
                      ))}
                      {isOwn && (
                        <>
                          <Box
                            sx={{ width: 1, height: 18, background: 'rgba(0,0,0,0.1)', mx: '2px' }}
                          />
                          <Box
                            component="button"
                            data-testid="advisor-chat-edit-btn"
                            title="Edit"
                            onClick={() => {
                              setEditingCreatedAt(msg.createdAt);
                              setEditDraft(msg.content);
                              setActionBarMsgId(null);
                            }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 26,
                              height: 26,
                              background: 'transparent',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              '&:hover': { background: 'rgba(0,0,0,0.06)' },
                            }}>
                            <svg
                              width={13}
                              height={13}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#333333"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </Box>
                          <Box
                            component="button"
                            data-testid="advisor-chat-delete-btn"
                            title="Delete"
                            onClick={() => {
                              sendDeleteMessage(msg.createdAt);
                              setActionBarMsgId(null);
                            }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 26,
                              height: 26,
                              background: 'transparent',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              '&:hover': { background: 'rgba(0,0,0,0.06)' },
                            }}>
                            <svg
                              width={13}
                              height={13}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#333333"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                              <path d="M10 11v6M14 11v6" />
                              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                            </svg>
                          </Box>
                        </>
                      )}
                    </Box>
                  )}
                  <MessageBubble
                    message={msg}
                    isOwn={isOwn}
                    advisorName={msgAdvisorName}
                    advisorPhotoUrl={msgAdvisorPhotoUrl}
                    showAvatar={!isOwn && showAvatar}
                    showName={!isOwn && showName}
                    currentUserId={user?.id}
                    onToggleReaction={(createdAt, emoji, action) =>
                      sendReaction(createdAt, emoji, action)
                    }
                    showNewMessageCue={newIncomingCueMessageId === msg.messageId}
                  />
                </Box>
              )}
            </Box>
          );
        })}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input area */}
      <Box sx={{ px: '20px', pt: '10px', pb: '20px', flexShrink: 0 }}>
        {/* Staged attachments — inside input area */}
        {stagedPreviews.length > 0 && (
          <Box sx={{ mb: 1, pb: 1, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            {isUploading && (
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  mb: 1,
                  height: 3,
                  borderRadius: 2,
                  backgroundColor: 'var(--tx-fill-2)',
                  '& .MuiLinearProgress-bar': { backgroundColor: '#C0FF02' },
                }}
              />
            )}
            <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {stagedPreviews.map((preview, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    px: '8px',
                    py: '5px',
                    borderRadius: '8px',
                    border: 0,
                    backgroundColor: 'var(--tx-fill)',
                    maxWidth: 200,
                  }}>
                  {preview.objectUrl ? (
                    <Box
                      component="img"
                      src={preview.objectUrl}
                      alt={preview.file.name}
                      sx={{
                        width: 20,
                        height: 20,
                        objectFit: 'cover',
                        borderRadius: '4px',
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <InsertDriveFileOutlined
                      sx={{ fontSize: 16, color: 'rgba(243,243,243,0.5)', flexShrink: 0 }}
                    />
                  )}
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: 'var(--tx-muted)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flex: 1,
                    }}>
                    {preview.file.name}
                  </Typography>
                  <IconButton
                    onClick={() => removeStagedFile(index)}
                    disabled={isUploading}
                    size="small"
                    sx={{
                      p: 0,
                      width: 14,
                      height: 14,
                      flexShrink: 0,
                      '&:hover': { backgroundColor: 'transparent' },
                    }}>
                    <Close sx={{ fontSize: 12, color: 'rgba(243,243,243,0.4)' }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            backgroundColor: 'var(--tx-glass-panel)',
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
            borderRadius: '24px',
            px: '14px',
            height: 54,
          }}>
          <IconButton
            data-testid="advisor-chat-attach-btn"
            component="label"
            disabled={isUploading || !group?.id}
            sx={{
              width: 32,
              height: 32,
              flexShrink: 0,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
            }}>
            <AttachFile
              sx={{
                fontSize: 18,
                color: isUploading ? 'var(--tx-faint)' : 'var(--tx-faint)',
                transform: 'rotate(45deg)',
              }}
            />
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept="image/jpeg,image/png,application/pdf"
              onChange={handleFileInputChange}
            />
          </IconButton>
          <InputBase
            data-testid="advisor-chat-message-input"
            inputRef={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (e.target.value.length > 0) setNewIncomingCueMessageId(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder={isAdvisor ? 'Message your client...' : 'Message your advisor...'}
            sx={{
              flex: 1,
              color: 'var(--tx-text)',
              fontSize: 14,
              '& input': {
                fontSize: 14,
              },
              '& input::placeholder': {
                color: 'var(--tx-faint)',
                opacity: 1,
                fontSize: 14,
              },
            }}
          />
          <IconButton
            data-testid="advisor-chat-send-btn"
            onClick={() => handleSend(inputValue)}
            disabled={isUploading || (!inputValue.trim() && stagedAttachments.length === 0)}
            sx={{
              width: 32,
              height: 32,
              backgroundColor:
                inputValue.trim() || stagedAttachments.length > 0
                  ? '#C0FF02'
                  : 'rgba(255,255,255,0.08)',
              '&:hover': {
                backgroundColor:
                  inputValue.trim() || stagedAttachments.length > 0
                    ? '#D4FF4D'
                    : 'rgba(255,255,255,0.12)',
              },
              '&.Mui-disabled': {
                backgroundColor: 'var(--tx-fill-2)',
              },
              transition: 'background-color 0.15s ease',
            }}>
            <Send
              sx={{
                fontSize: 16,
                color:
                  inputValue.trim() || stagedAttachments.length > 0
                    ? '#262626'
                    : 'rgba(255,255,255,0.3)',
                transform: 'rotate(-45deg)',
              }}
            />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default AdvisorChatTab;
